import { useId } from 'react'

import { motion } from 'motion/react'

import { TABLE_CHECKBOX_PATHS } from './table-checkbox-icons'

interface AnimatedTableCheckboxIconProps {
  checked: boolean
  indeterminate: boolean
}

const FILL_TRANSITION = { duration: 0.15 } as const
const CHECK_TRANSITION = { duration: 0.25, ease: 'easeOut' } as const

export function AnimatedTableCheckboxIcon({
  checked,
  indeterminate
}: AnimatedTableCheckboxIconProps) {
  const id = useId()
  const tickClipId = `${id}-tick`
  const dashClipId = `${id}-dash`
  const maskId = `${id}-mask`
  const selected = checked || indeterminate

  return (
    <svg
      data-slot='checkbox-icon'
      data-state={
        indeterminate ? 'indeterminate' : checked ? 'checked' : 'unchecked'
      }
      viewBox='0 0 24 24'
      fill='none'
      aria-hidden='true'
      focusable='false'
      className='size-6'
    >
      <defs>
        <clipPath id={tickClipId}>
          <path d={TABLE_CHECKBOX_PATHS.check} />
        </clipPath>
        <clipPath id={dashClipId}>
          <path d={TABLE_CHECKBOX_PATHS.indeterminate} />
        </clipPath>
        <mask
          id={maskId}
          maskUnits='userSpaceOnUse'
          x='0'
          y='0'
          width='24'
          height='24'
          style={{ maskType: 'luminance' }}
        >
          <rect width='24' height='24' fill='white' />
          <g clipPath={`url(#${tickClipId})`}>
            <motion.path
              d='M7.75 11.95L10.6 14.8L16.25 9.15'
              fill='none'
              stroke='black'
              strokeWidth='4'
              strokeLinecap='round'
              strokeLinejoin='round'
              initial={false}
              animate={{
                pathLength: checked && !indeterminate ? 1 : 0,
                opacity: checked && !indeterminate ? 1 : 0
              }}
              transition={CHECK_TRANSITION}
            />
          </g>
          <g clipPath={`url(#${dashClipId})`}>
            <motion.path
              d='M8 12H16'
              fill='none'
              stroke='black'
              strokeWidth='4'
              strokeLinecap='round'
              initial={false}
              animate={{
                pathLength: indeterminate ? 1 : 0,
                opacity: indeterminate ? 1 : 0
              }}
              transition={CHECK_TRANSITION}
            />
          </g>
        </mask>
      </defs>
      <motion.path
        d={TABLE_CHECKBOX_PATHS.outline}
        fill='currentColor'
        initial={false}
        animate={{ opacity: selected ? 0 : 1 }}
        transition={FILL_TRANSITION}
      />
      <motion.path
        d={TABLE_CHECKBOX_PATHS.filled}
        fill='currentColor'
        mask={`url(#${maskId})`}
        initial={false}
        animate={{ opacity: selected ? 1 : 0 }}
        transition={FILL_TRANSITION}
      />
    </svg>
  )
}
